import React, { FC, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useTranslation } from '$hooks';
import {
    FlowStep,
    ICalculatedGiftDetailsModel,
    IFlowPickup,
    IFlowSend,
    IGiftShopFlowData,
    IGiftShopToBasketBundle,
} from '$models';
import { useBasket } from '~/store';
import { addDeliveryStepToBundle, editDeliveryStep, getBundleFromBasket, getDefaultBundle } from '../helpers';
import { GiftShopDelivery } from './gift-shop-delivery';
import { basketPageUrl } from '$constants';
import { mq } from '$lib/helpers';
import { Text } from '$components/elements/text';
import { Loading } from '$components/elements/loading';

type Props = {
    itemNumber?: string;
    editStep?: string | FlowStep;
    onCloseModal?: () => void;
};

export const GiftShopFlow: FC<Props> = ({ itemNumber, editStep, onCloseModal }): JSX.Element => {
    const { translate } = useTranslation();
    const { push } = useRouter();
    const [giftShopBasketBundle, setGiftShopBasketBundle] = useState<IGiftShopToBasketBundle>();
    const { basket, updateGiftDetails, addGiftBundleToBasket } = useBasket();
    const [isLoading, setIsLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setGiftShopBasketBundle(editStep ? getBundleFromBasket(basket) : getDefaultBundle(itemNumber));
    }, []);

    const onSubmit = (data: IGiftShopFlowData) => {
        switch (data.kind) {
            case 'send':
            case 'pickup':
                handleDeliveryStep(data);
                break;
        }
    };

    const handleDeliveryStep = async (data: IFlowSend | IFlowPickup) => {
        if (editStep) {
            setIsLoading(true);
            const bundle = editDeliveryStep(data, giftShopBasketBundle);
            setGiftShopBasketBundle(bundle);
            const success = await updateGiftDetails(bundle?.giftDetails as ICalculatedGiftDetailsModel);
            if (success) {
                onCloseModal?.();
            }
            setIsLoading(false);
        } else {
            setGiftShopBasketBundle(addDeliveryStepToBundle(data, giftShopBasketBundle));
            addCardsStepToBundle();
        }
    };

    const addCardsStepToBundle = async () => {
        setIsLoading(true);
        const bundle = { ...giftShopBasketBundle };

        setGiftShopBasketBundle(bundle);
        const success = await addGiftBundleToBasket(bundle, itemNumber);
        if (success) {
            push(basketPageUrl);
        } else {
            setIsLoading(false);
        }
    };

    return (
        <OuterContainer ref={containerRef}>
            <StyledText variant="display6">{translate('giftShop.flow.deliveryHeader')}</StyledText>
            <GiftShopDelivery
                editStep={!!editStep}
                flowData={giftShopBasketBundle}
                onSubmit={(value) => onSubmit(value)}
                onCloseModal={() => onCloseModal?.()}
            />

            {isLoading && <Loading showBackdrop />}
        </OuterContainer>
    );
};

const OuterContainer = styled.div(({ theme }) => ({
    padding: `0 ${theme.space[6]} 160px ${theme.space[6]}`,

    [mq('sm')]: {
        padding: `0 ${theme.space[9]} 180px ${theme.space[9]}`,
    },
}));

const StyledText = styled(Text)(({ theme }) => ({
    marginBottom: theme.space[3],
    display: 'block',
}));
